/**
 * Calendar toolbar — week navigation + timezone selector.
 * Layout: [‹ ›] [Today] Title .................. [Timezone ▼]
 *
 * Ref: KInterviewScheduler/.../SchCalToolbar.tsx
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex, Icon, Box } from '@chakra-ui/react';
import { Button, Select, Text } from '@kula-ai/components';
import { ChevronLeft } from '@kula-ai/icons/outline/ChevronLeft';
import { ChevronRight } from '@kula-ai/icons/outline/ChevronRight';
import { CheckCircle } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components/text';
import type FullCalendar from '@fullcalendar/react';
import moment from 'moment-timezone';
import { useTzOptions } from '@/components/formBuilder/formFields/DateField/components/useTzOptions';
import {
  resolveTimezoneOptionValue,
  type TimezoneOption,
} from './timezoneOptions';
import { isNowInVisibleWeek } from './events/eventHelpers';

/** Live wall-clock for a timezone option — refreshes every minute. */
interface CalendarToolbarProps {
  calendarRef: React.RefObject<FullCalendar | null>;
  timezone: string;
  onTimezoneChange?: (_tz: string) => void;
  disablePastNavigation?: boolean;
  showTimezoneSelect?: boolean;
  /** Ref for datesSet subscription — toolbar registers its updateLabel here */
  onDatesSetRef?: React.MutableRefObject<(() => void) | null>;
}

export const CalendarToolbar = ({
  calendarRef,
  timezone,
  onTimezoneChange,
  disablePastNavigation = false,
  showTimezoneSelect = true,
  onDatesSetRef,
}: CalendarToolbarProps) => {
  const [dateLabel, setDateLabel] = useState('');
  const [isCurrentWeek, setIsCurrentWeek] = useState(true);

  // Filtered to zones moment has data for — the toolbar renders a live clock
  // per row and `moment().tz(key)` throws on an unknown key.
  const { timeZoneOptions } = useTzOptions() as {
    timeZoneOptions: TimezoneOption[];
  };
  const timezoneOptions = useMemo(
    () => timeZoneOptions.filter((o) => moment.tz.zone(o.value) !== null),
    [timeZoneOptions]
  );

  // Per-option "current time" labels. Computed for ALL options at once
  // on a single 60s tick — previously each of the ~140 option rows
  // mounted its own `setInterval`, so opening the dropdown spent ~140ms
  // laying out those effects and the open-scroll landed mid-layout,
  // making the menu visibly jump from the selected option to the top.
  // A precomputed map renders each row as plain text → list settles in
  // one frame → scroll-to-selected lands cleanly.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const timezoneTimes = useMemo(() => {
    const map: Record<string, string> = {};
    for (const opt of timezoneOptions) {
      map[opt.value] = moment(nowMs).tz(opt.value).format('hh:mm a');
    }
    return map;
  }, [timezoneOptions, nowMs]);

  const updateLabel = useCallback(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    const viewTitle =
      (api as unknown as { currentData?: { viewTitle?: string } })?.currentData
        ?.viewTitle ?? '';
    setDateLabel(viewTitle);

    const view = api.view;
    setIsCurrentWeek(
      isNowInVisibleWeek(view.activeStart, view.activeEnd, timezone)
    );
  }, [calendarRef, timezone]);

  useEffect(() => {
    const timer = setTimeout(updateLabel, 100);
    return () => clearTimeout(timer);
  }, [updateLabel]);

  // Register updateLabel so datesSet can call it without React re-render
  useEffect(() => {
    if (onDatesSetRef) {
      onDatesSetRef.current = updateLabel;
    }
    return () => {
      if (onDatesSetRef) onDatesSetRef.current = null;
    };
  }, [onDatesSetRef, updateLabel]);

  const handlePrev = useCallback(() => {
    setTimeout(() => {
      calendarRef.current?.getApi()?.prev();
      updateLabel();
    }, 0);
  }, [calendarRef, updateLabel]);

  const handleNext = useCallback(() => {
    setTimeout(() => {
      calendarRef.current?.getApi()?.next();
      updateLabel();
    }, 0);
  }, [calendarRef, updateLabel]);

  const handleToday = useCallback(() => {
    setTimeout(() => {
      calendarRef.current?.getApi()?.today();
      updateLabel();
    }, 0);
  }, [calendarRef, updateLabel]);

  const isPrevDisabled = disablePastNavigation && isCurrentWeek;

  // Select expects a string value that matches one of the option keys.
  // `Intl.DateTimeFormat().resolvedOptions().timeZone` returns legacy
  // aliases like `Asia/Calcutta` on some systems, but our option list
  // only carries the canonical `Asia/Kolkata` — so unknown values get
  // normalised. Values already in the list are kept as-is (see
  // `resolveTimezoneOptionValue`): normalising them would collapse
  // Singapore onto Kuala Lumpur.
  const timezoneValue = resolveTimezoneOptionValue(timezone, timezoneOptions);

  return (
    <Flex
      minH="60px"
      alignItems="center"
      px="10px"
      justifyContent="space-between"
      position="relative"
      zIndex={11}
      flexShrink={0}
    >
      {/* Left: nav arrows + today + title */}
      <Flex gap="6px" alignItems="center" flexShrink={0} minH="34px">
        {/* Navigation arrows — bordered pair */}
        <Flex alignItems="center" h="34px">
          <Flex
            alignItems="center"
            justifyContent="center"
            onClick={isPrevDisabled ? undefined : handlePrev}
            _hover={
              isPrevDisabled
                ? undefined
                : { cursor: 'pointer', bg: 'primary-25' }
            }
            border="1px solid"
            borderRight="none"
            borderColor="border-color"
            borderLeftRadius="sm"
            w="36px"
            h="34px"
            opacity={isPrevDisabled ? 0.4 : 1}
            cursor={isPrevDisabled ? 'not-allowed' : 'pointer'}
          >
            <Icon as={ChevronLeft} color="secondary-700" w="16px" h="16px" />
          </Flex>
          <Flex
            alignItems="center"
            justifyContent="center"
            onClick={handleNext}
            _hover={{ cursor: 'pointer', bg: 'primary-25' }}
            border="1px solid"
            borderColor="border-color"
            borderRightRadius="sm"
            w="36px"
            h="34px"
            cursor="pointer"
          >
            <Icon as={ChevronRight} color="secondary-700" w="16px" h="16px" />
          </Flex>
        </Flex>

        {/* Today button — tertiary (blue bg) when current week, quaternary (outlined) when not */}
        <Button
          variant={isCurrentWeek ? 'tertiary' : 'quaternary'}
          onClick={isCurrentWeek ? undefined : handleToday}
        >
          Today
        </Button>

        {/* Title — FullCalendar's view title */}
        <KText variant="heading_2" ml="6px" lineHeight="29.97px">
          {dateLabel}
        </KText>
      </Flex>

      {/* Right: timezone select */}
      {showTimezoneSelect && (
        <Box maxW="300px" flex={1}>
          <Select
            options={timezoneOptions}
            value={timezoneValue}
            onChange={(value) => {
              if (typeof value === 'string' && value) onTimezoneChange?.(value);
            }}
            searchable
            size="sm"
            renderOption={(option, { isSelected }) => (
              <Flex justify="space-between" align="center" w="100%" gap="3">
                <Box flex="1" minW={0}>
                  <Text variant="p" color="default" truncate>
                    {option.label}
                  </Text>
                </Box>
                <Flex align="center" gap="2" flexShrink={0}>
                  <Text variant="small" color="muted">
                    {timezoneTimes[(option as TimezoneOption).value]}
                  </Text>
                  {isSelected && (
                    <Box as="span" color="primary-500" lineHeight={0}>
                      <CheckCircle size={18} />
                    </Box>
                  )}
                </Flex>
              </Flex>
            )}
            // kula-ai positions the menu via floating-ui anchored at the
            // trigger's bottom-start, so `right`/`left` in customStyles
            // get overwritten. `menuWidthInset` is the only knob that
            // resizes + re-centres: negative = wider than trigger, kept
            // centred under it.
            menuWidthInset={-170}
            // kula's built-in scroll-to-selected is unreliable across
            // repeat opens (only fires on the first open in 1.10.x), so
            // disable it and scroll manually on open. With the option
            // rows now rendering precomputed time text (no per-row effect
            // / interval), the list settles in one frame and the two-rAF
            // scroll lands on the selected option instead of mid-layout.
            scrollToSelectedOnOpen={false}
            reactSelectProps={{
              onMenuOpen: () => {
                // Scroll the selected option into view on EVERY open
                // (kula's built-in only fires on the first open). Retry
                // across a few frames so it still lands if the menu list
                // hasn't finished laying out yet on a slow machine —
                // `scrollIntoView` is idempotent once settled and only
                // ever scrolls toward the selected option, so there's no
                // visible jump. The list itself now settles in one frame
                // (option times are precomputed, no per-row effects).
                let frame = 0;
                const scrollToSelected = () => {
                  document
                    .querySelector<HTMLElement>('[data-selected="true"]')
                    ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                  if (frame++ < 4) requestAnimationFrame(scrollToSelected);
                };
                requestAnimationFrame(scrollToSelected);
              },
            }}
          />
        </Box>
      )}
    </Flex>
  );
};
